import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Checkbox,
  Button,
  Stepper,
  Step,
  StepContent,
  StepConnector,
  styled,
  StepLabel,
  stepConnectorClasses,
  StepIconProps,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import moneyBagSrc from 'assets/images/moneyBag.png';
import editIcon from 'assets/images/EditDocument.png';
import CheckIcon from '@mui/icons-material/Check';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useSurveyContent } from '../../../../shared/hooks';
import {
  useHandleSubmit,
  useOnAlertClose,
  useOnboardingSelectors,
} from '../../survey/onboarding/Home/hooks';
import { redirectToUrl } from '../../survey/onboarding/Home/helpers';
import titleCase from '../../../../shared/helpers/titleCase';
import { surveyPortalMixpanel } from 'vendor/mixpanel';
import { ROUTES } from 'shared/routes/constants';
import { Link } from 'react-router-dom';

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 11,
    padding: 0,
    position: 'relative',
    top: '-18px', // Move line upwards (adjust as needed)
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderLeft: 'none',
    minHeight: 12,
    marginTop: '-8px', // Pull the line closer to the step circle
    [theme.breakpoints.down('sm')]: {
      minHeight: 12,
      marginTop: '-16px',
    },
  },
  [`&.MuiStepConnector-vertical:nth-of-type(2) .${stepConnectorClasses.line}`]: {
    borderLeft: '2px dotted rgba(112, 138, 144, 1)',
  },
}));

const CustomStepIcon: React.FC<StepIconProps> = (props) => {
  const { active, completed, className, icon } = props;
  return (
    <div
      className={className}
      style={{
        backgroundColor: active
          ? 'rgba(187, 230, 240, 1)'
          : completed
          ? 'gray'
          : 'rgba(38, 43, 45, 0.12)',
        color: active ? 'rgba(38, 43, 45, 1)' : 'rgba(114, 117, 119, 1)',
        borderRadius: '50%',
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 400,
        fontSize: '0.875rem',
      }}
    >
      {icon}
    </div>
  );
};

const CompactStepper = styled(Stepper)(({ theme }) => ({
  '& .MuiStep-root': {
    padding: 0, // reduce vertical gap between steps
    paddingBottom: '16px',
  },
  '& .MuiStepLabel-root': {
    marginBottom: 0, // reduce gap under the label
    padding: 0,
  },
  '& .MuiStepContent-root': {
    marginLeft: 12, // pull content closer to label
    paddingLeft: 8,
    paddingRight: 0,
    paddingBottom: 0,
  },
  '& .MuiStepLabel-label': {
    fontSize: '14px',
    fontWeight: 700,
  },
  '&& .MuiStepLabel-label.Mui-active': {
    fontSize: '14px',
    fontWeight: 700,
  },
  '& .MuiTypography-caption': {
    fontSize: '0.85rem', // smaller subtitle
  },
}));

const Onboarding: React.FC = () => {
  const { surveyContent } = useSurveyContent();
  const showHints = surveyContent?.content?.hints && surveyContent?.content?.hints.length > 0;
  const hasScreener = surveyContent?.content?.has_screener;
  const screenerDuration = surveyContent?.content?.screener_duration ?? 0;
  const hints = surveyContent?.content?.hints ?? [];
  const eligibility =
    surveyContent?.content?.eligibility_message ??
    'You’ll be a good fit if the following describes you:';
  const userName = surveyContent?.subject_given_name ?? '';
  const professionalType = surveyContent?.content?.professional_type ?? '';
  const surveyObjective = surveyContent?.content?.survey_objective ?? '';
  const surveyCompensation = surveyContent?.content?.survey_compensation ?? '';
  const surveyDuration = surveyContent?.content?.survey_duration ?? '';
  const [expandedAccordion, setExpandedAccordion] = React.useState<string[]>(['panel0']);
  const [hasAcceptedTermsAndCondition, setHasAcceptedTermsAndCondition] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [isOnboarding, setIsOnboarding] = React.useState(false);
  const { redirect_to } = useOnboardingSelectors();
  const [showWarning, setShowWarning] = React.useState(false);
  const handleSubmit = useHandleSubmit();
  const handleOnAlertClose = useOnAlertClose(setShowWarning);
  const changeTermAndConditions = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleOnAlertClose();
    setHasAcceptedTermsAndCondition(e.target.checked);
  };

  React.useEffect(() => {
    if (redirect_to && surveyContent?.subject) {
      const url = new URL(redirect_to);
      url.searchParams.set('SSEID', surveyContent.subject);
      url.searchParams.set('survey_id', surveyContent.subject);
      if (surveyContent.survey_host) {
        url.searchParams.set('survey_host', surveyContent.survey_host);
      }
      const timer = setTimeout(() => {
        redirectToUrl(url.toString());
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [redirect_to, surveyContent]);

  const hasTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRef.current && surveyContent?.subject) {
      surveyPortalMixpanel.track('Onboarding Page Viewed', {
        SSEID: surveyContent.subject,
        survey_id: surveyContent.survey_id,
        survey_host: surveyContent.survey_host,
      });
      hasTrackedRef.current = true;
    }
  }, [surveyContent]);

  const finishOnboarding = () => {
    const formData = {
      did_accept_terms: hasAcceptedTermsAndCondition,
      survey_request_url: window.location.href,
    };
    if (hasAcceptedTermsAndCondition) {
      setIsOnboarding(true);
      surveyPortalMixpanel.track('Start Button Clicked', {
        SSEID: surveyContent?.subject,
        survey_id: surveyContent?.survey_id,
        survey_host: surveyContent?.survey_host,
        source: 'Survey',
        button_location: 'Onboarding page',
      });
    }
    handleSubmit(formData, setShowWarning);
  };
  const steps = [
    {
      label: 'Screener',
      subtitle: `Determines your eligibility (about ${screenerDuration} minutes)`,
      description: ``,
    },
    {
      label: 'Main survey',
      subtitle: 'Completing these questions leads to payment',
      description: '',
    },
    {
      label: 'Get paid',
      subtitle: 'Instant payment upon completing main survey',
      description: ``,
    },
  ];

  return (
    <>
      {isOnboarding && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            px: 2,
          }}
        >
          <Typography variant="h3" fontWeight="400" mb="32px">
            {hasScreener ? 'Let’s confirm that you’re eligible...' : 'Loading main survey...'}
          </Typography>
          <CircularProgress />
        </Box>
      )}
      {!isOnboarding && (
        <Box maxWidth="480px">
          <Box
            height={'calc(100svh - 260px)'}
            overflow="auto"
            sx={{
              borderBottom: '1px solid #e0e0e0',
              paddingRight: '12px',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              msOverflowStyle: 'none',
            }}
          >
            <Typography variant="h2" fontWeight={700} marginBottom="16px">
              Let’s get started, {userName.length > 1 ? titleCase(userName) : ''}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb="32px">
              Thanks for your interest. For this survey, we’re reaching out to {professionalType}.
              The goal of this survey is to learn about {surveyObjective}.
            </Typography>

            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" alignItems="center" spacing="6px">
                <Box
                  component={'img'}
                  sx={{
                    width: '24px',
                  }}
                  alt="moneyBag"
                  src={moneyBagSrc}
                ></Box>
                <Typography variant="body1" fontWeight="700">
                  Comp.: ${surveyCompensation}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing="6px">
                <AccessTimeIcon width="24px" />
                <Typography variant="body1" fontWeight="700">
                  Time: {surveyDuration} mins
                </Typography>
              </Stack>
            </Stack>

            {showHints && (
              <Box marginBottom="16px">
                <Box>
                  <Typography variant="body1" color="text.secondary" mb="16px">
                    {eligibility}
                  </Typography>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {hints.map((hint, index) => (
                      <li
                        key={index}
                        style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}
                      >
                        <CheckIcon
                          sx={{
                            color: 'rgba(42, 158, 86, 1)',
                            width: '24px',
                            height: '24px',
                            marginRight: '8px',
                          }}
                        />
                        <Typography variant="body1" color="text.secondary">
                          {hint}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              </Box>
            )}

            <Box
              sx={{ background: 'white', borderRadius: '12px', padding: '16px', paddingBottom: 0 }}
            >
              <Typography variant="h3" fontWeight={700} mb="24px">
                What’s ahead?
              </Typography>

              {hasScreener && (
                <Box mb="32px">
                  <CompactStepper
                    activeStep={activeStep}
                    orientation="vertical"
                    connector={<CustomConnector />}
                  >
                    {steps.map((step, index) => (
                      <Step key={step.label}>
                        <StepLabel
                          StepIconComponent={CustomStepIcon}
                          optional={
                            <Typography variant="subtitle2" fontWeight={400}>
                              {step.subtitle}
                            </Typography>
                          }
                        >
                          {step.label}
                        </StepLabel>
                        <StepContent>
                          <Typography>{step.description}</Typography>
                        </StepContent>
                      </Step>
                    ))}
                  </CompactStepper>
                </Box>
              )}
              {!hasScreener && (
                <Box mb="32px">
                  <Stack direction="row" alignItems="center" spacing="16px" mb="16px">
                    <Box
                      component={'img'}
                      sx={{
                        width: '24px',
                      }}
                      alt="editIcon"
                      src={editIcon}
                    ></Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="700">
                        Complete Survey
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        Share your expertise and point of view
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing="16px">
                    <CreditCardIcon width="24px" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="700">
                        Get Paid
                      </Typography>
                      <Typography variant="subtitle2" fontWeight="400">
                        Instant compensation upon completion
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: '42px', display: 'flex' }}>
            <Checkbox
              data-testid="termsCheckBox"
              checked={hasAcceptedTermsAndCondition}
              onChange={changeTermAndConditions}
              sx={{ ml: '-6px', mt: '-10px' }}
            />
            <Typography variant="body1" display="inline" sx={{ fontSize: '12px' }}>
              I accept the{' '}
              <Typography
                variant="body1"
                component={Link}
                to={ROUTES.SURVEY.PATHS.TERMS}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'underline', fontSize: '12px' }}
              >
                Terms & Conditions
              </Typography>{' '}
              and{' '}
              <Typography
                variant="body1"
                component={Link}
                to={ROUTES.SURVEY.PATHS.TERMS}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'underline', fontSize: '12px' }}
              >
                Privacy Policy
              </Typography>
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => finishOnboarding()}
            sx={{ mt: '8px' }}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: '15px !important' }} />}
          >
            {hasScreener ? 'START SCREENER' : 'START SURVEY'}
          </Button>
          {showWarning && (
            <Box sx={{ display: 'flex', flexDirection: 'row', ml: '3px', gap: '9px', mt: '8px' }}>
              <Typography
                sx={{
                  color: 'rgba(209, 34, 73, 1)',
                  fontWeight: '400',
                  fontSize: '12px',
                }}
                data-test-id="onboarding-alert"
              >
                Please accept the Terms & Conditions and Privacy Policy
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default Onboarding;
